import { VirtualCanvas }         from "./virtual.canvas";
import { CanvasResizeInterface } from "./canvas.resize.interface";

/**
 *
 * @class CanvasObject
 *
 * all objects in our canvas must implement this common interface so that
 * they can be used with any of the canvas adaptors we choose.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2024
 *
 */
export class CanvasObject implements CanvasResizeInterface {

  /*
   * we track position and bound, so that in groups, parent and children can
   * be positioned relatively.
   *
   */

  /** horizontal position */
  public x: number                             = 0;

  /** vertical position */
  public y: number                             = 0;

  /** horizontal extent */
  public width: number                         = 10;

  /** vertical extent */
  public height: number                        = 10;

  /** the canvas that this object renders on to */
  private readonly _canvas: VirtualCanvas|null = null;

  /** the parent object (if any) */
  private _parent: CanvasObject|null           = null;

  /** the children (if any) */
  private _children: (CanvasObject)[]          = [];

  /**
   * create a new canvas object. top level objects are linked to the canvas to allow
   * for global notifications (like window resizing).
   *
   * @param canvas      VirtualCanvas all objects must render to a canvas
   * @param childObject boolean       only top level "main" objects should be set to true
   *
   */
  constructor(canvas: VirtualCanvas|null, childObject: boolean = false) {

    this._canvas = canvas;

    if (childObject) {

      /* nothing more to do, children are positioned relative to their parents  */

      return ;
    }

    /*
     * top level objects register with the canvas, so we can get notifications like onResize()
     * and know to automatically update our own layout.
     *
     */

    this._canvas?.registerResizable(this);

  }

  /** fetch the canvas */
  canvas(): VirtualCanvas|null {

    return this._canvas;
  }

  /** fetch the current parent (if any) */
  parent(): CanvasObject|null {

    return this._parent;
  }

  /** fetch the current children (if any) */
  children(): CanvasObject[] {

    return this._children;
  }

  /** fetch the first child (if there is one) */
  first(): CanvasObject|null {

    return this.children().length ? this.children()[0] : null;
  }

  /** check if we have any children */
  hasChildren(): boolean {

    return this._children && this._children.length > 0;
  }

  /** force the parent for this object */
  setParent(obj: CanvasObject|null, noRemove: boolean = false): CanvasObject|null {

    /* if there is already a parent, unlink it */

    if (this.parent() && !noRemove) {

      this.parent()?.removeChildren(this);
    }

    /* set the new parent */

    this._parent = obj;

    return this._parent;
  }

  /** unlink a child object */
  removeChildren(obj: CanvasObject|(CanvasObject|null)[]|null): CanvasObject {

    if (!obj) {

      /* nothing to do */

      return this;
    }

    let items: (CanvasObject|null)[];

    if (Array.isArray(obj)) {

      items = obj;

    } else {

      items = [obj];
    }

    let co: CanvasObject = this;

    items.forEach((item: CanvasObject|null): void => {

      co._children.filter((x: CanvasObject): boolean => x != item);

      item?.setParent(null, true);

    });

    return this;
  }

  /** add children */
  addChildren(obj: CanvasObject|(CanvasObject|null)[]|null): CanvasObject {

    if (!obj) {

      /* nothing to do */

      return this;
    }

    let items: (CanvasObject|null)[];

    if (Array.isArray(obj)) {

      items = obj;

    } else {

      items = [obj];
    }

    let co: CanvasObject = this;

    items.forEach((item: CanvasObject|null): void => {

      if (!item) {

        /* don't add null children */

        return ;
      }

      /* don't add duplicates  */

      if (co._children.find((x: CanvasObject|null): boolean => x == item)) {

        return ;
      }

      co._children.push(item);

      item.setParent(this);

    });

    return this;
  }

  /** upon return object should have updated its layout or geometry as needed. */
  public layout(): CanvasObject {

    /* subclasses must implement */

    return this;
  }

  /** this canvas has resized, client object should reevaluate its layout/geometry */
  public resize(width: number, height: number): void {

    this.layout();
  }

  /** convenience to update the bounding rect */
  public boundUpdate(geometry: {x: number, y: number, width: number, height: number}): CanvasObject {

    this.x      = geometry.x;
    this.y      = geometry.y;
    this.width  = geometry.width;
    this.height = geometry.height;

    return this;
  }
}
